import {
  Bot,
  Paperclip,
  Plus,
  Send,
  Sparkles,
  Upload,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useChatMessages } from '../hooks/useChatMessages';
import { CONNECTORS, formatFileSize } from '../utils/helpers';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import './ChatInterface.css';

const PLAN_STORAGE_KEY = 'dummyChatSelectedPlan';
const EMAIL_STORAGE_KEY = 'dummyChatSelectedEmail';
const AREA_ENABLED_KEY = 'dummyChatAreaEnabled';
const DISABLE_TRAINING_KEY = 'dummyChatDisableTraining';
const AREA_KEYS = ['textarea', 'input', 'contenteditable'];
const AREA_CONFIG = {
  textarea: {
    label: '\ud83d\udcdd Textarea',
    textClass: 'area-label-textarea',
    checkClass: 'area-check-textarea',
  },
  input: {
    label: '\u2500 Input',
    textClass: 'area-label-input',
    checkClass: 'area-check-input',
  },
  contenteditable: {
    label: '\u270f ContentEditable',
    textClass: 'area-label-ce',
    checkClass: 'area-check-ce',
  },
};

const ChatHeader = ({
  selectedEmail,
  selectedPlan,
  onEmailChange,
  onPlanChange,
  disableTrainingOnData,
  onDisableTrainingChange,
}) => (
  <header className="chat-header" data-testid="chat-header">
    <div className="header-content">
      <div className="header-left">
        <div className="icon-wrapper">
          <Sparkles className="sparkles-icon" />
        </div>
        <h1 className="header-title" data-testid="chat-title">
          AI Chat Assistant (React)
        </h1>
      </div>
      <div className="header-right">
        <select
          value={selectedEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          className="plan-selector"
          data-testid="email-selector"
        >
          <option value="">Not logged in</option>
          <option value="nishchay202@gmail.com">nishchay202@gmail.com</option>
          <option value="nishchay.rajput@singulr.ai">
            nishchay.rajput@singulr.ai
          </option>
        </select>
        <select
          value={selectedPlan}
          onChange={(e) => onPlanChange(e.target.value)}
          className="plan-selector"
          data-testid="plan-selector"
          disabled={!selectedEmail}
        >
          <option value="">Select Plan</option>
          <option value="free">Free Plan</option>
          <option value="paid">Paid Plan</option>
          <option value="enterprise">Enterprise Plan</option>
        </select>
        <label
          className="disable-training-toggle"
          title="Disable training on data"
          data-testid="disable-training-toggle"
        >
          <input
            type="checkbox"
            checked={disableTrainingOnData}
            onChange={(e) => onDisableTrainingChange(e.target.checked)}
            className="disable-training-checkbox"
          />
          No Training
        </label>
        <Button
          variant="outline"
          size="sm"
          className="new-chat-btn"
          data-testid="new-chat-button"
        >
          + New Chat
        </Button>
        <a href="/" className="multi-input-link">
          ← Single Input
        </a>
      </div>
    </div>
  </header>
);

const EmptyState = () => (
  <div className="empty-state" data-testid="empty-state">
    <div className="empty-state-icon-wrapper">
      <Sparkles className="empty-state-icon" />
    </div>
    <h2 className="empty-state-title">How can I help you today?</h2>
    <p className="empty-state-subtitle">
      Ask me anything or upload files to get started
    </p>
  </div>
);

const MessageAttachment = ({ attachment, messageRole }) => (
  <div
    className={`message-attachment ${messageRole === 'user' ? 'user-attachment' : 'assistant-attachment'}`}
    data-testid="attachment-item"
  >
    <Paperclip className="attachment-icon" />
    <span className="attachment-name">{attachment.name}</span>
    <span className="attachment-size">({formatFileSize(attachment.size)})</span>
  </div>
);

const Message = ({ message }) => (
  <div
    className={`message ${message.role === 'user' ? 'message-user' : 'message-assistant'}`}
    data-testid={`message-${message.role}`}
  >
    {message.role === 'assistant' && (
      <div className="avatar avatar-assistant">
        <Bot className="avatar-icon" />
      </div>
    )}
    <div
      className={`message-bubble ${message.role === 'user' ? 'bubble-user' : 'bubble-assistant'}`}
      data-testid="message-content"
    >
      {message.attachments && message.attachments.length > 0 && (
        <div className="message-attachments" data-testid="message-attachments">
          {message.attachments.map((attachment) => (
            <MessageAttachment
              key={attachment.id}
              attachment={attachment}
              messageRole={message.role}
            />
          ))}
        </div>
      )}
      <p className="message-text" data-testid="message-text">
        {message.content}
      </p>
      <span className="message-timestamp">
        {message.timestamp.toLocaleTimeString()}
      </span>
    </div>
    {message.role === 'user' && (
      <div className="avatar avatar-user">
        <User className="avatar-icon" />
      </div>
    )}
  </div>
);

const LoadingIndicator = () => (
  <div className="loading-indicator" data-testid="loading-indicator">
    <div className="avatar avatar-assistant">
      <Bot className="avatar-icon" />
    </div>
    <div className="loading-bubble">
      <div className="loading-dots">
        <div className="loading-dot" />
        <div className="loading-dot loading-dot-delay-1" />
        <div className="loading-dot loading-dot-delay-2" />
      </div>
    </div>
  </div>
);

const AttachmentPreview = ({ attachment, onRemove }) => {
  console.log('Rendering AttachmentPreview for', attachment);
  return (
  <div className="attachment-preview" data-testid="attachment-preview">
    <Paperclip className="preview-icon" />
    <span className="preview-name" data-testid="attachment-name">
      {attachment.name}
    </span>
    <span className="preview-size" data-testid="attachment-size">
      ({formatFileSize(attachment.size)})
    </span>
    <button
      onClick={onRemove}
      className="preview-remove"
      data-testid="remove-attachment-button"
    >
      <X className="remove-icon" />
    </button>
  </div>
)};

const Toast = ({ toast }) => {
  if (!toast.visible) return null;
  const classMap = {
    success: 'xhr-toast-success',
    error: 'xhr-toast-error',
    warning: 'xhr-toast-warning',
  };
  const iconMap = { success: '✓', error: '✕', warning: '⚠' };
  return (
    <div
      className={`xhr-toast ${classMap[toast.type] ?? 'xhr-toast-error'}`}
      data-testid="xhr-toast"
    >
      <span className="xhr-toast-icon">{iconMap[toast.type] ?? '!'}</span>
      <span className="xhr-toast-message">{toast.message}</span>
    </div>
  );
};

const ConnectorSelector = ({ onSelect, onClose }) => (
  <div className="connector-panel" data-testid="connectors-panel">
    <div className="connector-header">
      <span className="connector-title">Select Connector</span>
      <button
        onClick={onClose}
        className="connector-close"
        data-testid="close-connectors-button"
      >
        <X className="close-icon" />
      </button>
    </div>
    <div className="connector-grid">
      {CONNECTORS.map((connector) => (
        <button
          key={connector.id}
          onClick={() => onSelect(connector)}
          className="connector-button"
          data-testid={`connector-${connector.id}`}
        >
          <span className="connector-icon">{connector.icon}</span>
          <span className="connector-name">{connector.name}</span>
        </button>
      ))}
    </div>
  </div>
);

export const ChatInterface = () => {
  const [areaValues, setAreaValues] = React.useState({
    textarea: '',
    input: '',
    contenteditable: '',
  });
  const [areaFiles, setAreaFiles] = React.useState({
    textarea: [],
    input: [],
    contenteditable: [],
  });
  const [areaEnabled, setAreaEnabled] = React.useState({
    textarea: true,
    input: true,
    contenteditable: true,
  });
  const [draggingOverKey, setDraggingOverKey] = React.useState(null);
  const [disableTrainingOnData, setDisableTrainingOnData] =
    React.useState(true);
  const [showConnectors, setShowConnectors] = React.useState(false);
  const [selectedEmail, setSelectedEmail] = React.useState('');
  const [selectedPlan, setSelectedPlan] = React.useState('');

  const areaDragCounters = useRef({
    textarea: 0,
    input: 0,
    contenteditable: 0,
  });
  const textareaRef = useRef(null);
  const contentEditableRef = useRef(null);
  const fileInputRefTA = useRef(null);
  const fileInputRefIN = useRef(null);
  const fileInputRefCE = useRef(null);
  const scrollAreaRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [xhrAsync, setXhrAsync] = React.useState(true);
  const [xhrMethod, setXhrMethod] = React.useState('POST');
  const [fetchMethod, setFetchMethod] = React.useState('POST');
  const [toast, setToast] = React.useState({
    visible: false,
    message: '',
    type: 'success',
  });

  const HTTP_METHODS = [
    'GET',
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
    'HEAD',
    'OPTIONS',
  ];

  const { messages, isLoading, sendMessage } = useChatMessages();
  const syncSettings = async (email, plan, disableTraining) => {
    try {
      const payload = {
        singulr_dummy_test_app: {
          email: email || null,
          plan: plan || null,
          disable_training_on_data: disableTraining,
        },
      };

      const response = await fetch('https://httpbin.org/post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      console.log('Settings updated:', data);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleEmailChange = async (email) => {
    setSelectedEmail(email);

    try {
      if (email) {
        window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      } else {
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving email to localStorage:', error);
    }

    // If email is cleared, also clear plan; if a new email is set and
    // no plan is selected yet, default the plan to "free" and cache it.
    let nextPlan = selectedPlan;
    if (!email) {
      nextPlan = '';
      setSelectedPlan('');
      try {
        window.localStorage.removeItem(PLAN_STORAGE_KEY);
      } catch (error) {
        console.error('Error clearing plan from localStorage:', error);
      }
    } else if (!selectedPlan) {
      nextPlan = 'free';
      setSelectedPlan('free');
      try {
        window.localStorage.setItem(PLAN_STORAGE_KEY, 'free');
      } catch (error) {
        console.error('Error saving default plan to localStorage:', error);
      }
    }

    await syncSettings(email, nextPlan, disableTrainingOnData);
  };

  const handlePlanChange = async (plan) => {
    if (!selectedEmail) {
      return;
    }
    setSelectedPlan(plan);
    try {
      if (plan) {
        window.localStorage.setItem(PLAN_STORAGE_KEY, plan);
      } else {
        window.localStorage.removeItem(PLAN_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving plan to localStorage:', error);
    }
    await syncSettings(selectedEmail, plan, disableTrainingOnData);
  };

  const handleDisableTrainingChange = async (value) => {
    setDisableTrainingOnData(value);
    try {
      window.localStorage.setItem(DISABLE_TRAINING_KEY, JSON.stringify(value));
    } catch {}
    await syncSettings(selectedEmail, selectedPlan, value);
  };

  // Send initial plan on mount
  useEffect(() => {
    try {
      const savedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      const savedPlan = window.localStorage.getItem(PLAN_STORAGE_KEY);

      const initialEmail = savedEmail || '';
      let initialPlan = '';

      if (
        initialEmail &&
        (savedPlan === 'free' ||
          savedPlan === 'paid' ||
          savedPlan === 'enterprise')
      ) {
        initialPlan = savedPlan;
      } else if (initialEmail && !initialPlan) {
        // Email exists but no valid saved plan -> default to "free" and cache it
        initialPlan = 'free';
        try {
          window.localStorage.setItem(PLAN_STORAGE_KEY, 'free');
        } catch (error) {
          console.error('Error saving default plan to localStorage:', error);
        }
      }

      setSelectedEmail(initialEmail);
      setSelectedPlan(initialPlan);

      const savedEnabled = window.localStorage.getItem(AREA_ENABLED_KEY);
      if (savedEnabled) {
        try {
          const parsed = JSON.parse(savedEnabled);
          if (parsed && typeof parsed === 'object') {
            setAreaEnabled((prev) => ({
              textarea:
                typeof parsed.textarea === 'boolean'
                  ? parsed.textarea
                  : prev.textarea,
              input:
                typeof parsed.input === 'boolean' ? parsed.input : prev.input,
              contenteditable:
                typeof parsed.contenteditable === 'boolean'
                  ? parsed.contenteditable
                  : prev.contenteditable,
            }));
          }
        } catch {}
      }

      const savedDisableTraining =
        window.localStorage.getItem(DISABLE_TRAINING_KEY);
      let initialDisableTraining = false;
      if (savedDisableTraining !== null) {
        try {
          initialDisableTraining = JSON.parse(savedDisableTraining) === true;
        } catch {}
      }
      setDisableTrainingOnData(initialDisableTraining);

      // Send initial settings
      syncSettings(initialEmail, initialPlan, initialDisableTraining);
      return;
    } catch (error) {
      console.error('Error reading settings from localStorage:', error);
    }
    // Fallback: no email, no plan
    syncSettings('', '', false);
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // type: 'success' | 'error' | 'warning'
  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, message, type });
    toastTimerRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 10000);
  };

  const makeAttachments = (rawFiles) =>
    Array.from(rawFiles).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

  const addFilesToArea = (key, rawFiles) => {
    const attachments = makeAttachments(rawFiles);
    setAreaFiles((prev) => ({
      ...prev,
      [key]: [...prev[key], ...attachments],
    }));
  };

  const removeAreaFile = (key, id) => {
    setAreaFiles((prev) => ({
      ...prev,
      [key]: prev[key].filter((a) => a.id !== id),
    }));
  };

  const handleAreaDragEnter = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    areaDragCounters.current[key] = (areaDragCounters.current[key] || 0) + 1;
    setDraggingOverKey(key);
  };

  const handleAreaDragLeave = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    areaDragCounters.current[key] = (areaDragCounters.current[key] || 1) - 1;
    if (areaDragCounters.current[key] <= 0) {
      areaDragCounters.current[key] = 0;
      setDraggingOverKey((prev) => (prev === key ? null : prev));
    }
  };

  const handleAreaDrop = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    areaDragCounters.current[key] = 0;
    setDraggingOverKey(null);
    const droppedFiles = e.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0)
      addFilesToArea(key, droppedFiles);
  };

  const toggleAreaEnabled = (key) => {
    setAreaEnabled((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        window.localStorage.setItem(AREA_ENABLED_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const getFileInputRef = (key) =>
    key === 'textarea'
      ? fileInputRefTA
      : key === 'input'
        ? fileInputRefIN
        : fileInputRefCE;

  const handleAreaFileSelect = (key, e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    addFilesToArea(key, selectedFiles);
    e.target.value = '';
  };

  const handleAreaPaste = (key, e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const fileItems = Array.from(items).filter((item) => item.kind === 'file');
    if (fileItems.length === 0) return;
    const pastedFiles = fileItems
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFilesToArea(key, pastedFiles);
    }
  };

  const handleXhrClick = () => {
    // ── Case 5: XHR prototype patched? ─────────────────────────────
    const openNative = XMLHttpRequest.prototype.open
      .toString()
      .includes('[native code]');
    const sendNative = XMLHttpRequest.prototype.send
      .toString()
      .includes('[native code]');
    if (!openNative || !sendNative) {
      showToast(
        `⚠ [XHR PROTOTYPE PATCHED] XMLHttpRequest.prototype.${
          !openNative ? 'open' : 'send'
        } has been overridden by an extension or injected script.`,
        'warning'
      );
    }
    const methodUpper = xhrMethod.toUpperCase();
    const pathMap = {
      HEAD: 'get',
      OPTIONS: 'get',
      DELETE: 'delete',
      GET: 'get',
      POST: 'post',
      PUT: 'put',
      PATCH: 'patch',
    };
    const url = `https://httpbin.org/${pathMap[methodUpper] ?? 'post'}`;
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(methodUpper);
    const payload = hasBody
      ? JSON.stringify({
          singulr_xhr_test: {
            method: methodUpper,
            timestamp: new Date().toISOString(),
          },
        })
      : null;

    if (!xhrAsync) {
      // ── SYNC path ────────────────────────────────────────────────
      // After xhr.send(false) returns, the spec guarantees readyState===4
      // and status!==0. If an extension/injector intercepted and async-ified
      // the request those invariants break — we detect and warn about it.
      const xhr = new XMLHttpRequest();
      try {
        xhr.open(methodUpper, url, false); // false = synchronous
        if (hasBody) xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(payload);

        if (xhr.readyState !== XMLHttpRequest.DONE || xhr.status === 0) {
          // Request is still in-flight → something converted it to async
          showToast(
            `⚠ [SYNC→ASYNC HIJACK] ${methodUpper} — A browser extension intercepted this ` +
              `synchronous XHR and made it asynchronous. No synchronous response is available.`,
            'warning'
          );
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          showToast(
            `[SYNC] ${methodUpper} → ${xhr.status} ${xhr.statusText}`,
            'success'
          );
        } else {
          // Non-2xx: status is available synchronously, raise as error
          throw new Error(`HTTP ${xhr.status} ${xhr.statusText}`);
        }
      } catch (error) {
        // Catches both network errors (browser throws) and our own throw above
        showToast(`[SYNC] ${methodUpper} → ${error.message}`, 'error');
      }
      return;
    }

    // ── ASYNC path ──────────────────────────────────────────────────
    const xhr = new XMLHttpRequest();
    xhr.open(methodUpper, url, true); // true = asynchronous (default)
    if (hasBody) xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.timeout = 10000;

    xhr.onload = () => {
      // onload fires for ALL completed responses including 4xx/5xx
      if (xhr.status >= 200 && xhr.status < 300) {
        showToast(
          `[ASYNC] ${methodUpper} → ${xhr.status} ${xhr.statusText}`,
          'success'
        );
      } else {
        // HTTP errors: status is known, this is NOT a network error
        showToast(
          `[ASYNC] ${methodUpper} → HTTP ${xhr.status} ${xhr.statusText}`,
          'error'
        );
      }
    };
    xhr.onerror = () => {
      // Fires ONLY on network-level failure (DNS, CORS block, connection refused)
      // Never fires for HTTP 4xx/5xx — those come through onload
      showToast(
        `[ASYNC] ${methodUpper} → Network error (no response received)`,
        'error'
      );
    };
    xhr.ontimeout = () => {
      showToast(
        `[ASYNC] ${methodUpper} → Request timed out after 10s`,
        'error'
      );
    };
    xhr.send(payload);
  };

  const handleFetchClick = async () => {
    const methodUpper = fetchMethod.toUpperCase();
    const pathMap = {
      HEAD: 'get',
      OPTIONS: 'get',
      DELETE: 'delete',
      GET: 'get',
      POST: 'post',
      PUT: 'put',
      PATCH: 'patch',
    };
    const url = `https://httpbin.org/${pathMap[methodUpper] ?? 'post'}`;
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(methodUpper);
    const body = hasBody
      ? JSON.stringify({
          singulr_fetch_test: {
            method: methodUpper,
            timestamp: new Date().toISOString(),
          },
        })
      : undefined;

    // ── Case 2: Is window.fetch native or patched? ──────────────────
    const fetchIsNative = window.fetch.toString().includes('[native code]');
    if (!fetchIsNative) {
      showToast(
        `⚠ [FETCH PATCHED] window.fetch has been replaced by an extension or injected script. ` +
          `Requests may be intercepted, modified, or blocked.`,
        'warning'
      );
    }

    try {
      const response = await window.fetch(url, {
        method: methodUpper,
        headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
        body,
      });

      // ── Case 3: Response body consumed before we read it ─────────
      if (response.bodyUsed) {
        showToast(
          `⚠ [FETCH BODY CONSUMED] ${methodUpper} → response.bodyUsed is true before we read it. ` +
            `An interceptor has already consumed the response stream.`,
          'warning'
        );
        return;
      }

      // ── Case 6: Unexpected redirect ──────────────────────────────
      if (
        response.redirected &&
        !response.url.startsWith('https://httpbin.org')
      ) {
        showToast(
          `⚠ [UNEXPECTED REDIRECT] ${methodUpper} → Response was redirected to ${response.url} ` +
            `by an extension or service worker.`,
          'warning'
        );
      }

      // ── Case 7: Opaque / error response from service worker ────────
      if (response.type === 'opaque' || response.type === 'error') {
        showToast(
          `⚠ [SERVICE WORKER / CORS] ${methodUpper} → Response type is '${response.type}'. ` +
            `A service worker or CORS policy blocked full access to the response.`,
          'warning'
        );
        return;
      }

      // ── Case 4: HTTP error — fetch does NOT throw on 4xx/5xx ───────
      if (!response.ok) {
        showToast(
          `✕ [FETCH] ${methodUpper} → HTTP ${response.status} ${response.statusText} ` +
            `(fetch does not throw on HTTP errors — always check response.ok)`,
          'error'
        );
        return;
      }

      showToast(
        `✓ [FETCH] ${methodUpper} → ${response.status} ${response.statusText}`,
        'success'
      );
    } catch (err) {
      // fetch only throws on true network failure (no response at all)
      showToast(
        `✕ [FETCH] ${methodUpper} → Network error: ${err.message}`,
        'error'
      );
    }
  };

  const handleAreaSend = async (key) => {
    const text = areaValues[key].trim();
    const areaFileList = areaFiles[key];
    if (!text && areaFileList.length === 0) return;

    const currentText = areaValues[key];
    setAreaValues((prev) => ({ ...prev, [key]: '' }));
    if (key === 'contenteditable' && contentEditableRef.current) {
      contentEditableRef.current.innerText = '';
    }
    setAreaFiles((prev) => ({ ...prev, [key]: [] }));
    await sendMessage(currentText, areaFileList);
  };

  const handleAreaKeyDown = (key, e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAreaSend(key);
    }
  };

  const handleConnectorSelect = (connector) => {
    setAreaValues((prev) => ({
      ...prev,
      textarea: `${prev.textarea}${prev.textarea ? ' ' : ''}@${connector.name} `,
    }));
    setShowConnectors(false);
    textareaRef.current?.focus();
  };

  return (
    <div className="chat-page">
      <div className="chat-container" data-testid="chat-container">
        <ChatHeader
          selectedEmail={selectedEmail}
          selectedPlan={selectedPlan}
          onEmailChange={handleEmailChange}
          onPlanChange={handlePlanChange}
          disableTrainingOnData={disableTrainingOnData}
          onDisableTrainingChange={handleDisableTrainingChange}
        />

        <ScrollArea
          className="messages-area"
          ref={scrollAreaRef}
          data-testid="messages-scroll-area"
        >
          <div className="messages-wrapper" data-testid="messages-container">
            {messages.length === 0 && <EmptyState />}
            {messages.map((message) => (
              <Message key={message.id} message={message} />
            ))}
            {isLoading && <LoadingIndicator />}
          </div>
        </ScrollArea>

        <div className="input-section" data-testid="input-area">
          <div className="input-wrapper">
            {/* Per-area enable/disable toggles */}
            <div className="area-enable-row">
              <span className="area-enable-label">Show:</span>
              {AREA_KEYS.map((key) => {
                const cfg = AREA_CONFIG[key];
                return (
                  <label
                    key={key}
                    className={`area-enable-item ${cfg.textClass}`}
                  >
                    <input
                      type="checkbox"
                      checked={areaEnabled[key]}
                      onChange={() => toggleAreaEnabled(key)}
                      className={cfg.checkClass}
                    />
                    {cfg.label}
                  </label>
                );
              })}
            </div>

            {showConnectors && (
              <ConnectorSelector
                onSelect={handleConnectorSelect}
                onClose={() => setShowConnectors(false)}
              />
            )}

            {/* Three simultaneous independent input areas */}
            <div className="multi-area-container" data-testid="input-container">
              {AREA_KEYS.map((key) => {
                if (!areaEnabled[key]) return null;
                const cfg = AREA_CONFIG[key];
                const files = areaFiles[key];
                const value = areaValues[key];
                const fileRef = getFileInputRef(key);
                return (
                  <div
                    key={key}
                    className={`area-card area-card-${key}${draggingOverKey === key ? ' area-card-dragging' : ''}`}
                    data-testid={`input-area-${key}`}
                    onDragEnter={(e) => handleAreaDragEnter(key, e)}
                    onDragLeave={(e) => handleAreaDragLeave(key, e)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleAreaDrop(key, e)}
                  >
                    {draggingOverKey === key && (
                      <div
                        className="area-drop-indicator"
                        data-testid="drag-drop-overlay"
                      >
                        <Upload className="area-drop-icon" />
                        Drop to attach to {cfg.label}
                      </div>
                    )}
                    {files.length > 0 && (
                      <div
                        className="attachments-list"
                        data-testid="attachment-preview-list"
                      >
                        {files.map((att) => (
                          <AttachmentPreview
                            key={att.id}
                            attachment={att}
                            onRemove={() => removeAreaFile(key, att.id)}
                          />
                        ))}
                      </div>
                    )}
                    <div className="input-container">
                      <div className="textarea-wrapper">
                        {key === 'textarea' && (
                          <Textarea
                            ref={textareaRef}
                            value={value}
                            onChange={(e) =>
                              setAreaValues((prev) => ({
                                ...prev,
                                textarea: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => handleAreaKeyDown(key, e)}
                            onPaste={(e) => handleAreaPaste(key, e)}
                            placeholder="Type your message... (Shift+Enter for new line)"
                            className="message-textarea"
                            data-testid="message-input"
                          />
                        )}
                        {key === 'input' && (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) =>
                              setAreaValues((prev) => ({
                                ...prev,
                                input: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => handleAreaKeyDown(key, e)}
                            onPaste={(e) => handleAreaPaste(key, e)}
                            placeholder="Type your message..."
                            className="message-input-single"
                            data-testid="message-input"
                          />
                        )}
                        {key === 'contenteditable' && (
                          <div
                            ref={contentEditableRef}
                            contentEditable
                            suppressContentEditableWarning
                            role="textbox"
                            tabIndex={0}
                            aria-multiline="true"
                            data-placeholder="Type your message... (Shift+Enter for new line)"
                            data-testid="message-input"
                            onInput={(e) => {
                              const text = e.currentTarget.innerText;
                              setAreaValues((prev) => ({
                                ...prev,
                                contenteditable: text,
                              }));
                            }}
                            onKeyDown={(e) => handleAreaKeyDown(key, e)}
                            onPaste={(e) => handleAreaPaste(key, e)}
                            className="message-contenteditable"
                          />
                        )}
                        <div className="input-actions">
                          {key === 'textarea' && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="action-button"
                              onClick={() => setShowConnectors(!showConnectors)}
                              title="Add connector"
                              data-testid="toggle-connectors-button"
                            >
                              <Plus className="action-icon" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="action-button"
                            onClick={() => fileRef.current?.click()}
                            title="Attach file"
                            data-testid="attach-file-button"
                          >
                            <Paperclip className="action-icon" />
                          </Button>
                          <input
                            ref={fileRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => handleAreaFileSelect(key, e)}
                            data-testid={`file-input-${key}`}
                          />
                        </div>
                      </div>
                      <Button
                        onClick={() => handleAreaSend(key)}
                        disabled={!value.trim() && files.length === 0}
                        size="icon"
                        className="send-button"
                        data-testid="send-button"
                      >
                        <Send className="send-icon" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="xhr-controls" data-testid="xhr-controls">
              <span className="xhr-section-label">XHR</span>
              <select
                value={xhrMethod}
                onChange={(e) => setXhrMethod(e.target.value)}
                className="xhr-method-select"
                data-testid="xhr-method-select"
              >
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`xhr-mode-toggle ${xhrAsync ? 'xhr-mode-async' : 'xhr-mode-sync'}`}
                onClick={() => setXhrAsync((v) => !v)}
                data-testid="xhr-mode-toggle"
                title="Toggle XHR mode"
              >
                {xhrAsync ? 'Async' : 'Sync'}
              </button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="xhr-button"
                onClick={handleXhrClick}
                data-testid="xhr-request-button"
              >
                Send XHR
              </Button>

              <span className="xhr-section-label fetch-label">Fetch</span>
              <select
                value={fetchMethod}
                onChange={(e) => setFetchMethod(e.target.value)}
                className="fetch-method-select"
                data-testid="fetch-method-select"
              >
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="fetch-button"
                onClick={handleFetchClick}
                data-testid="fetch-request-button"
              >
                Send Fetch
              </Button>
            </div>
            <Toast toast={toast} />
            <p className="input-hint">
              Press Enter to send • Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
